import * as React from "react";
import { Modal, Button, Loader } from "semantic-ui-react";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";

import { isRequestActive } from "redux-request-loading";

import { IsLoadingButton } from "@teamprevent/common/IsLoadingButton";
import { navigateToParentRoute } from "@teamprevent/common/routing/actions";

import { Input } from "@neworbit/simpleui-input";

import { routeIdSelector as customerIdSelector } from "../../crud/customer/selectors";
import { routeIdSelector as employeeRecordIdSelector } from "../../crud/employeeRecord/selectors";
import { CREATE_MHP_TREATMENT_CASE } from "../actionTypes";
import { mhpTreatmentCaseApi } from "../mhpTreatmentCaseApi";
import { TaskType } from "../../task/model";
import { createMHPTreatmentCase } from "../actions";
import { CreateMHPCaseTreatmentModel } from "../models";
import { ApplicationState } from "../../applicationState";

import { push } from "redux-little-router";

interface Props {
    linkedOhCaseId?: string;
    open: boolean;
    onClose?: () => void;
}

export const CreateMHPTreatmentCaseModal: React.FunctionComponent<Props> = (props: Props) => {

    const [isModalOpen, setIsModalOpen] = React.useState<boolean>(props.open);
    const [mhpCaseTaskTypes, setMHPTaskTypes] = React.useState<TaskType[]>([]);
    const [selectedTaskType, setSelectedTaskType] = React.useState<string>(null);
    const [loadingTaskTypes, setLoadingTaskTypes] = React.useState(false);
    const [submitted, setSubmitted] = React.useState(false);

    const customerId = useSelector(customerIdSelector);
    const employeeRecordId = useSelector(employeeRecordIdSelector);
    const creatingCase = useSelector((state: ApplicationState) => isRequestActive(state, CREATE_MHP_TREATMENT_CASE));

    const isLoading = React.useMemo(() => loadingTaskTypes || creatingCase, [loadingTaskTypes, creatingCase]);

    React.useEffect(() => {
        (async () => {
            setLoadingTaskTypes(true);
            setMHPTaskTypes((await mhpTreatmentCaseApi.getTaskTypes()));
            setLoadingTaskTypes(false);
        })();

        return () => resetState();
    }, []);

    React.useEffect(() => {
        setIsModalOpen(props.open);
    }, [props.open]);

    const resetState = () => {
        setIsModalOpen(false);
        setMHPTaskTypes([]);
        setSelectedTaskType(null);
        setLoadingTaskTypes(false);
        setSubmitted(false);
    };

    const dispatch = useDispatch();

    const onClose = () => {
        resetState();

        if (props.onClose) {
            props.onClose();
        }

        if (!props.linkedOhCaseId) {
            dispatch(navigateToParentRoute());
        }
    };

    const onCreate = async () => {
        setSubmitted(true);

        if (!selectedTaskType) {
            return;
        }

        const createModel: CreateMHPCaseTreatmentModel = {
            customerId,
            employeeRecordId,
            initialTaskType: selectedTaskType,
            linkedOhCaseId: props.linkedOhCaseId
        };
        const newOhCase = await dispatch(createMHPTreatmentCase(createModel));

        dispatch(push(`/customers/${customerId}/employeeRecords/${employeeRecordId}/ohCases/${newOhCase.id}`));

        resetState();
    };

    return (
        <Modal open={isModalOpen}>
            <Modal.Header content="Create MHP Treatment Case" />
            <Modal.Content>
                {isLoading ?
                    <Loader /> :
                    <Input.Dropdown
                        required
                        options={mhpCaseTaskTypes.map(t => ({ text: t.description, value: t.type }))}
                        showErrors={submitted}
                        value={selectedTaskType}
                        onChange={setSelectedTaskType}
                    />
                }
            </Modal.Content>
            <Modal.Actions>
                <Button content="Cancel" onClick={onClose} disabled={isLoading} />
                <IsLoadingButton content="Create" onClick={onCreate} requestNames={CREATE_MHP_TREATMENT_CASE} disabled={isLoading} />
            </Modal.Actions>
        </Modal>
    );
};
